import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoOutlinedIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
);

const LeadsOverTimeChartJS = ({ data = [], dropdown = null }) => {
    const chartRef = useRef();

    // Helper function to safely format dates
    const formatDate = (dateInput) => {
        if (!dateInput) return '';

        try {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateInput);
            return 'Invalid Date';
        }
    };    // Create default data for empty state - only 3 lead types
    const getDefaultData = () => {
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(now.getDate() - 29); // Start from 30 days ago
        const data = [];

        // Generate data points for each day
        for (let date = new Date(startDate); date <= now; date.setDate(date.getDate() + 1)) {
            data.push({
                date: formatDate(date),
                leadCaptureForm: 0,
                csv: 0,
                manuallyAdded: 0,
                total: 0
            });
        }

        return data;
    };    // Process the leads data
    const processData = () => {
        if (!data || data.length === 0) {
            console.log('📊 No data provided, returning default data');
            return getDefaultData();
        }

        console.log('📊 Processing leads data:', {
            dataLength: data.length,
            sampleData: data.slice(0, 2),
            dataTypes: data.map(item => Object.keys(item)).slice(0, 2)
        });

        let processedData = data.map(item => {
            const leadCaptureForm = item.leadCaptureForm || 0;
            const csv = item.csv || 0;
            const manuallyAdded = item.manuallyAdded || 0;
            // Calculate total as sum of all sources to ensure accuracy
            const total = leadCaptureForm + csv + manuallyAdded;

            return {
                date: item.date || 'Unknown',
                leadCaptureForm,
                csv,
                manuallyAdded,
                total: item.total || total // Use backend total if available, otherwise calculate
            };
        });

        // Sort by date
        processedData.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA - dateB;
        });

        // Convert to cumulative totals (running sum)
        let cumulativeTotal = 0;
        const cumulativeData = processedData.map(item => {
            cumulativeTotal += item.total;
            return {
                ...item,
                total: cumulativeTotal
            };
        });

        console.log('📊 Processed data result:', {
            length: cumulativeData.length,
            sampleProcessed: cumulativeData.slice(0, 2),
            totalSum: cumulativeData[cumulativeData.length - 1]?.total || 0,
            hasValidData: cumulativeData.some(item => item.total > 0)
        });

        return cumulativeData;
    };

    // Process the data
    const chartData = useMemo(() => processData(), [data]);    // Calculate totals - sum all individual sources to get accurate total
    const totals = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return {
                total: 0,
                leadCaptureForm: 0,
                csv: 0,
                manuallyAdded: 0
            };
        }

        const result = chartData.reduce((acc, item) => {
            acc.leadCaptureForm += item.leadCaptureForm || 0;
            acc.csv += item.csv || 0;
            acc.manuallyAdded += item.manuallyAdded || 0;
            return acc;
        }, {
            total: 0,
            leadCaptureForm: 0,
            csv: 0,
            manuallyAdded: 0
        });

        // Calculate total as sum of all sources to ensure accuracy
        result.total = result.leadCaptureForm + result.csv + result.manuallyAdded;

        return result;
    }, [chartData]);    // Prepare Chart.js data - showing only total leads as one line
    const chartJSData = {
        labels: chartData.map(item => item.date),
        datasets: [
            {
                label: 'Leads',
                data: chartData.map(item => {
                    // Calculate total as sum of all sources to ensure accuracy
                    const total = (item.leadCaptureForm || 0) + (item.csv || 0) + (item.manuallyAdded || 0);
                    return item.total || total;
                }),
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                    gradient.addColorStop(0, 'rgba(41, 174, 248, 0.25)');
                    gradient.addColorStop(1, 'rgba(41, 174, 248, 0.05)');
                    return gradient;
                },
                borderColor: '#29AEF8',
                borderWidth: 3,
                borderCapStyle: 'round',
                borderJoinStyle: 'miter',
                pointRadius: 0,
                pointHoverRadius: 7,
                pointBackgroundColor: '#29AEF8',
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 3,
                pointHoverBackgroundColor: '#29AEF8',
                pointHoverBorderColor: '#FFFFFF',
                pointHoverBorderWidth: 3,
                fill: true,
                tension: 0,
                stepped: false,
                yAxisID: 'y',
            }
        ]
    };// Debug the chart data structure
    console.log('📊 LeadsOverTimeChartJS - Chart Data:', {
        labels: chartJSData.labels,
        datasets: chartJSData.datasets.map(ds => ({
            label: ds.label,
            data: ds.data,
            dataLength: ds.data.length,
            hasValidData: ds.data.some(val => val > 0),
            dataRange: [Math.min(...ds.data), Math.max(...ds.data)]
        })),
        chartData: chartData.slice(0, 3),
        totalDataPoints: chartData.length,
        hasAnyData: chartData.some(item => item.total > 0)
    });    // Enhanced multi-axis chart options with improved visuals
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1200,
            easing: 'easeInOutQuart',
            x: {
                duration: 0
            },
            y: {
                duration: 1200,
                easing: 'easeInOutQuart'
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        layout: {
            padding: {
                top: 10,
                right: 15,
                bottom: 5,
                left: 5
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                padding: 12,
                titleFont: {
                    size: 13,
                    weight: '600',
                    family: "'Inter', 'Roboto', sans-serif"
                },
                bodyFont: {
                    size: 12,
                    family: "'Inter', 'Roboto', sans-serif"
                },
                titleColor: '#FFFFFF',
                bodyColor: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        label += context.parsed.y || 0;
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'category',
                position: 'bottom',
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    drawBorder: false,
                    color: 'rgba(0, 0, 0, 0.05)',
                    lineWidth: 1
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 1
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                    color: '#6B7280',
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif",
                        weight: '400'
                    },
                    autoSkip: true,
                    maxTicksLimit: 10,
                    padding: 8,
                    callback: function (value, index, values) {
                        const label = this.getLabelForValue(value);
                        const date = new Date(label);
                        if (!isNaN(date)) {
                            return date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                            });
                        }
                        return label;
                    }
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    color: 'rgba(0, 0, 0, 0.05)',
                    lineWidth: 1
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 1
                },
                ticks: {
                    stepSize: 1,
                    precision: 0,
                    color: '#6B7280',
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif",
                        weight: '400'
                    },
                    padding: 8
                },
                title: {
                    display: false
                }
            }
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '414px',
                position: 'relative',
                borderRadius: '18px',
                backgroundColor: '#FFFFFF'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    padding: '32px 18px 26px 18px',
                    flexDirection: 'column'
                }}
            >
                {/* Header section */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333333',
                        fontSize: '12px',
                        gap: '5px'
                    }}
                >
                    {/* Dropdown as heading for team insights */}
                    {dropdown || (
                        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                                Leads Over Time
                            </Typography>
                            <Tooltip title="Lead generation over time with breakdown by source">
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#828282' }} />
                            </Tooltip>
                        </Box>
                    )}
                </Box>

                {/* Enhanced Legend */}
                <Box
                    sx={{
                        display: 'flex',
                        padding: '24px 0 20px 0',
                        gap: '20px',
                        flexWrap: 'wrap',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                        marginBottom: '16px'
                    }}
                >                    {[
                    { label: 'Leads', value: totals.total, color: '#29AEF8' }
                ].map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Box
                            sx={{
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: item.color,
                                borderRadius: '4px'
                            }}
                        >
                            <svg width="16" height="14" viewBox="0 0 8 6" fill="none">
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Box>
                        <Typography sx={{
                            fontSize: '13px',
                            color: '#374151',
                            fontWeight: '500',
                            fontFamily: "'Inter', 'Roboto', sans-serif"
                        }}>
                            {item.label} <span style={{ color: '#6B7280', fontWeight: '400' }}>({item.value})</span>
                        </Typography>
                    </Box>
                ))}
                </Box>

                {/* Chart */}
                <Box
                    sx={{
                        height: '260px',
                        width: '100%',
                        marginTop: '8px',
                        position: 'relative'
                    }}
                >                    <Line
                        ref={chartRef}
                        data={chartJSData}
                        options={options}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default LeadsOverTimeChartJS;
